<script setup lang="ts">
import { type PropType, ref, inject, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import { useMutation } from '@tanstack/vue-query';
import { patientsApi, type PatientListItem } from '@/api/patients';
import { type SerializedUser } from '@/api/users';
import type { AxiosError } from 'axios';
import { usePermissions } from '@/composables/usePermissions';

const { hasPermission } = usePermissions();

const { t } = useI18n();

const emit = defineEmits(['close', 'refetch', 'success']);

const props = defineProps({
  patient: {
    type: Object as PropType<PatientListItem>,
    required: true,
  },
  closeButtonText: {
    type: String,
    default: 'Cerrar',
  },
});

const currentUser = inject<Ref<SerializedUser>>('currentUser');
// Care-planning companies reactivate as a new episode (starts today); others keep the
// legacy plain desarchivar. The server decides by the same flag; this only sets the copy.
const newEpisode = !!currentUser?.value?.companyCarePlanningEnabled;

const { handleSubmit } = useForm();

const { mutate, isError } = useMutation<unknown, AxiosError>(
  {
    mutationFn: () => patientsApi.reactivate(props.patient.id),
    onSuccess: () => {
      emit('refetch');
      emit('success');
      emit('close');
    },
  },
);

const submitButtonDisabled = ref(false);
const onSubmit = handleSubmit(() => {
  submitButtonDisabled.value = true;
  mutate();
});
</script>
<template>
  <v-card
    v-if="hasPermission('patients', 'update')"
    class="w-100 pa-4"
    flat
  >
    <v-card-title>
      {{ t('patientUndiscardForm.title') }} {{ patient.fullName }}
    </v-card-title>
    <v-form
      class="w-100 ma-0"
      @submit.prevent="onSubmit"
    >
      <v-card flat>
        <v-card-text>
          {{ newEpisode ? t('patientUndiscardForm.confirmationNewEpisode') : t('patientUndiscardForm.confirmation') }}
        </v-card-text>
        <v-card-actions>
          <v-btn
            :text="props.closeButtonText"
            @click="$emit('close')"
          />
          <v-spacer />
          <v-btn
            color="red"
            type="submit"
            :disabled="submitButtonDisabled"
          >
            {{ newEpisode ? t('patientUndiscardForm.reactivate') : t('patientUndiscardForm.submit') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
    <v-alert
      v-if="isError"
      class="mt-4 rounded"
      :type="'error'"
    >
      {{ t('userSession.defaultError') }}
    </v-alert>
  </v-card>
</template>
